import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Plus, 
  Upload, 
  Download, 
  Trash2, 
  Eye,
  Building2,
  Wallet2,
  ArrowUpRight,
  ArrowUpDown,
  Layers,
  TrendingUp,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { portfolioTrackerService } from '@/services/portfolioTrackerService'
import { 
  PortfolioTrackerWithSummary,
  AllPortfoliosSummary,
  AllPortfoliosGraphData
} from '@/types/portfolio'
import { cn, formatMoney, formatPercentage } from '@/utils'
import { PageTemplate } from '@/components/templates'
import { Button, Input } from '@/components/atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CONTAINER_SIZES } from '@/constants'
import {
  PortfolioChart,
  PortfolioMetricCard,
  DonutChart,
  PortfolioInvestedBubbleChart,
  PortfolioGainLossMiniChart,
} from './components'
import { MiniAreaChart } from '@/components/molecules'
import { useDeviceType } from '@/hooks/useDeviceType'


const PortfolioTrackerScreen = () => {
  const navigate = useNavigate()
  const [portfolios, setPortfolios] = useState<PortfolioTrackerWithSummary[]>([])
  const [summary, setSummary] = useState<AllPortfoliosSummary | null>(null)
  const [graphData, setGraphData] = useState<AllPortfoliosGraphData | null>(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [newPortfolioName, setNewPortfolioName] = useState('')
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvPortfolioName, setCsvPortfolioName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const { isMobile } = useDeviceType()
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof document === 'undefined') {
      return false
    }
    return document.documentElement.classList.contains('dark')
  })

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const root = document.documentElement
    const syncTheme = () => {
      setIsDarkMode(root.classList.contains('dark'))
    }

    syncTheme()
    const observer = new MutationObserver(syncTheme)
    observer.observe(root, { attributes: true, attributeFilter: ['class'] })

    return () => {
      observer.disconnect()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [portfoliosData, summaryData, graphData] = await Promise.all([
        portfolioTrackerService.getPortfolios(),
        portfolioTrackerService.getAllPortfoliosSummary(),
        portfolioTrackerService.getAllPortfoliosGraph()
      ])
      
      // Load individual portfolio summaries for card data
      let portfoliosWithSummary: PortfolioTrackerWithSummary[] = portfoliosData.data || []
      
      if (portfoliosData.data && portfoliosData.data.length > 0) {
        const summaryPromises = portfoliosData.data.map(async (portfolio) => {
          try {
            const portfolioSummary = await portfolioTrackerService.getPortfolioSummary(portfolio.portfolio_id)
            return {
              ...portfolio,
              total_invested_amount: portfolioSummary.total_invested_amount,
              total_current_value: portfolioSummary.total_current_value,
              total_gain_loss_usd: portfolioSummary.total_gain_loss_usd,
              total_gain_loss_percentage: portfolioSummary.total_gain_loss_percentage,
              total_assets: portfolioSummary.total_assets
            }
          } catch {
            // If portfolio summary fails, return basic portfolio data
            return {
              ...portfolio,
              total_invested_amount: 0,
              total_current_value: 0,
              total_gain_loss_usd: 0,
              total_gain_loss_percentage: 0,
              total_assets: 0
            }
          }
        })
        
        portfoliosWithSummary = await Promise.all(summaryPromises)
      }
      
      setPortfolios(portfoliosWithSummary)
      setSummary(summaryData)
      setGraphData(graphData)
    } catch (err) {
      setError('Failed to load portfolios')
      console.error('Error loading portfolio data:', err)
      // Set empty defaults to prevent UI breaking
      setPortfolios([])
      setSummary({
        total_invested_amount: 0,
        total_current_value: 0,
        total_gain_loss_usd: 0,
        total_gain_loss_percentage: 0,
        total_assets: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) {
      setError('Portfolio name is required')
      return
    }

    try {
      setCreating(true)
      setError(null)
      
      const newPortfolio = await portfolioTrackerService.createPortfolio(newPortfolioName.trim())
      
      // Add new portfolio with summary data (empty portfolio)
      const newPortfolioWithSummary: PortfolioTrackerWithSummary = {
        ...newPortfolio,
        total_invested_amount: 0,
        total_current_value: 0,
        total_gain_loss_usd: 0,
        total_gain_loss_percentage: 0,
        total_assets: 0
      }
      
      setPortfolios(prev => [...(prev || []), newPortfolioWithSummary])
      setNewPortfolioName('')
      setShowCreateModal(false)
      setSuccessMessage('Portfolio created successfully!')
      
      // Reload summary data
      const summaryData = await portfolioTrackerService.getAllPortfoliosSummary()
      setSummary(summaryData)
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to create portfolio')
      console.error('Error creating portfolio:', err)
    } finally {
      setCreating(false)
    }
  }

  const handleImportCSV = async () => {
    if (!csvFile || !csvPortfolioName.trim()) {
      setError('Please select a CSV file and enter portfolio name')
      return
    }

    try {
      setImporting(true)
      setError(null)
      
      const result = await portfolioTrackerService.createPortfolioFromCSV(csvPortfolioName.trim(), csvFile)
      
      // Add the new portfolio to the list with summary data
      const newPortfolioWithSummary: PortfolioTrackerWithSummary = {
        portfolio_id: result.portfolio_id,
        name: result.name,
        created_at: result.created_at,
        updated_at: result.updated_at,
        total_invested_amount: 0,
        total_current_value: 0,
        total_gain_loss_usd: 0,
        total_gain_loss_percentage: 0,
        total_assets: result.matched_assets || 0
      }
      
      setPortfolios(prev => [...(prev || []), newPortfolioWithSummary])
      setCsvFile(null)
      setCsvPortfolioName('')
      setShowImportModal(false)
      setSuccessMessage(`Portfolio created with ${result.matched_assets} assets!`)
      
      // Reload summary data
      const summaryData = await portfolioTrackerService.getAllPortfoliosSummary()
      setSummary(summaryData)
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      if (portfolioTrackerService.isCSVError(err)) {
        setError(`CSV Error: ${err.error}`)
      } else {
        setError('Failed to import CSV')
      }
      console.error('Error importing CSV:', err)
    } finally {
      setImporting(false)
    }
  }

  const handleDeletePortfolio = async (portfolioId: string, portfolioName: string) => {
    if (!confirm(`Are you sure you want to delete portfolio "${portfolioName}"? This action cannot be undone.`)) {
      return
    }

    try {
      await portfolioTrackerService.deletePortfolio(portfolioId)
      setPortfolios(prev => (prev || []).filter(p => p.portfolio_id !== portfolioId))
      setSuccessMessage('Portfolio deleted successfully!')
      
      // Reload summary data
      const summaryData = await portfolioTrackerService.getAllPortfoliosSummary()
      setSummary(summaryData)
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to delete portfolio')
      console.error('Error deleting portfolio:', err)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type === 'text/csv') {
      setCsvFile(file)
      setError(null)
    } else {
      setError('Please select a valid CSV file')
      setCsvFile(null)
    }
  }

  const downloadCSVTemplate = () => {
    const template = `Project Name,Date of Investment,Invested Amount,Project Website,Project Valuation,Amount of equity or tokens,Terms
Example Project,2023-01-15,50000,https://example.com,1000000,100000,SAFE agreement`
    
    const blob = new Blob([template], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'portfolio_template.csv'
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const mobileSummaryCards = summary
    ? [
        {
          key: 'invested',
          label: 'Total Invested',
          value: formatMoney(summary.total_invested_amount),
          icon: Wallet2,
        },
        {
          key: 'current',
          label: 'Current Value',
          value: formatMoney(summary.total_current_value),
          icon: ArrowUpRight,
        },
        {
          key: 'gain',
          label: 'Total Gain/Loss',
          value: formatMoney(summary.total_gain_loss_usd),
          icon: ArrowUpDown,
          subValue: formatPercentage(summary.total_gain_loss_percentage, 0),
        },
        {
          key: 'assets',
          label: 'Total Assets',
          value: summary.total_assets.toString(),
          icon: Layers,
        },
      ]
    : []

  if (loading) {
    return <PageTemplate isLoading />
  }

  return (
    <PageTemplate maxWidth={isMobile ? CONTAINER_SIZES.FULL : CONTAINER_SIZES.WIDE}>
      <div className={cn(isMobile ? 'space-y-4' : 'space-y-6')}>
        <div className="flex flex-col items-start">
          <h1
            className={cn(
              "font-['Plus_Jakarta_Sans'] font-semibold tracking-[-0.28px] text-[#2d2923] dark:text-[#fdfdfd]",
              isMobile ? 'mb-0 text-[23px] leading-[32px]' : 'mb-2 text-[30px] leading-[40px]'
            )}
          >
            Portfolio Tracker
          </h1>
          <p
            className={cn(
              "font-['Plus_Jakarta_Sans'] font-normal text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]",
              isMobile ? 'text-[14px] leading-[20px]' : 'text-[16px] leading-[24px]'
            )}
          >
            Track and manage your investment portfolios across private markets
          </p>
        </div>
        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg"
          >
            {successMessage}
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg"
          >
            {error}
          </motion.div>
        )}

        {summary && (
          isMobile ? (
            <div className="grid grid-cols-2 gap-[6px]">
              {mobileSummaryCards.map((item) => {
                const Icon = item.icon
                const isCurrency = item.value.startsWith('$')
                const valueWithoutCurrency = isCurrency ? item.value.slice(1).trimStart() : item.value

                return (
                  <div
                    key={item.key}
                    className="border border-[rgba(45,41,35,0.08)] bg-white p-[16px] dark:border-[rgba(255,255,255,0.06)] dark:bg-[#1c1c1c]"
                  >
                    <div className="mb-[8px] flex flex-col gap-[6px]">
                      <Icon size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
                      <p className="font-['Plus_Jakarta_Sans'] text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        {item.label}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-[4px]">
                      <p
                        className={cn(
                          "font-['Plus_Jakarta_Sans'] font-medium tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd] whitespace-nowrap",
                          item.subValue ? 'text-[18px] leading-[28px]' : 'text-[20px] leading-[32px]'
                        )}
                      >
                        {isCurrency ? (
                          <span className="inline-flex items-baseline whitespace-nowrap">
                            <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>
                            <span className="ml-[2px]">{valueWithoutCurrency}</span>
                          </span>
                        ) : (
                          item.value
                        )}
                      </p>
                      {item.subValue && (
                        <div className="relative shrink-0 flex items-center gap-[4px] rounded-[16px] bg-[rgba(242,241,246,0.64)] pb-[4px] pl-[6px] pr-[8px] pt-[3px] dark:bg-[rgba(13,13,13,0.64)]">
                          <TrendingUp size={12} className="text-[#2ea66b]" />
                          <p className="font-['Plus_Jakarta_Sans'] text-[12px] font-semibold leading-[16px] text-[#2ea66b]">
                            {item.subValue}
                          </p>
                          <div className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_1px_1px_4px_0px_rgba(217,214,227,0.4)] dark:shadow-[inset_1px_1px_4px_0px_rgba(0,0,0,0.4)]" />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="mb-2 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
              <PortfolioMetricCard
                icon="invested"
                label="Total Invested"
                value={formatMoney(summary.total_invested_amount)}
                chart={<PortfolioInvestedBubbleChart />}
              />
              <PortfolioMetricCard
                icon="current"
                label="Current Value"
                value={formatMoney(summary.total_current_value)}
                chart={<MiniAreaChart />}
              />
              <PortfolioMetricCard
                icon="gain"
                label="Total Gain/Loss"
                value={formatMoney(summary.total_gain_loss_usd)}
                subValue={formatPercentage(summary.total_gain_loss_percentage, 0)}
                trend={summary.total_gain_loss_usd >= 0 ? 'up' : 'down'}
                chart={<PortfolioGainLossMiniChart />}
              />
              <PortfolioMetricCard
                icon="assets"
                label="Total Assets"
                value={summary.total_assets.toString()}
                chart={
                  <DonutChart
                    size={108}
                    strokeWidth={15}
                    segments={[
                      { value: 20, color: 'rgba(45,41,35,0.64)' },
                      { value: 7, color: '#2d2923' },
                      { value: 13, color: 'rgba(197,204,159,0.96)' },
                      { value: 60, color: '#c8ed03' },
                    ]}
                    segmentGapDegrees={4}
                    roundedCaps={false}
                    className="absolute left-[39px] top-[8px]"
                  />
                }
              />
            </div>
          )
        )}

        <div className="flex h-[52px] items-end">
          <h2
            className={cn(
              "font-['Plus_Jakarta_Sans'] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]",
              isMobile ? 'text-[20px] font-bold leading-[24px]' : 'text-[23px] font-semibold leading-[32px]'
            )}
          >
            All Portfolios Performance
          </h2>
        </div>
        <PortfolioChart graphData={graphData} loading={loading} mobile={isMobile} darkMode={isDarkMode} />

        {/* Portfolio List */}
        <div className="space-y-4">
          <div className={cn('items-start justify-between gap-4', isMobile ? 'flex flex-col' : 'flex flex-col sm:flex-row sm:items-center')}>
            <h2 className={cn("font-['Plus_Jakarta_Sans'] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]", isMobile ? 'text-[23px] font-semibold leading-[32px]' : 'text-[23px] font-semibold leading-[32px]')}>
              Your Portfolios
            </h2>
            
            <div className={cn('flex flex-wrap gap-[6px]', isMobile ? 'w-full' : '')}>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  'h-[32px] rounded-none px-4 text-[12px] font-medium',
                  isMobile ? 'w-[167px]' : '',
                  isDarkMode ? 'border-[rgba(255,255,255,0.12)] bg-[#0d0d0d] text-[#fdfdfd]' : 'border-[rgba(45,41,35,0.12)] bg-white text-[#2d2923]'
                )}
                leftIcon={<Download size={14} />}
                onClick={downloadCSVTemplate}
              >
                Download Template
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  'h-[32px] rounded-none px-4 text-[12px] font-medium',
                  isMobile ? 'w-[119px]' : '',
                  isDarkMode ? 'border-[rgba(255,255,255,0.12)] bg-[rgba(253,253,253,0.1)] text-[#fdfdfd]' : 'border-[rgba(45,41,35,0.12)] bg-[rgba(45,41,35,0.1)] text-[#2d2923]'
                )}
                leftIcon={<Upload size={14} />}
                onClick={() => setShowImportModal(true)}
              >
                Import CSV
              </Button>

              <Button
                variant="primary"
                size="sm"
                className={cn(
                  'h-[32px] rounded-none px-4 text-[12px] font-medium',
                  isMobile ? 'w-[152px]' : '',
                  isDarkMode ? 'bg-white text-[#0d0d0d] hover:bg-[#f3f3f3]' : 'bg-[#2d2923] text-white hover:bg-[#1f1c17]'
                )}
                leftIcon={<Plus size={14} />}
                onClick={() => navigate('/portfolio/create')}
              >
                Create Portfolio
              </Button>
            </div>
          </div>

          {!portfolios || portfolios.length === 0 ? (
            <Card variant="elevated">
              <CardContent className="p-12 text-center">
                <Building2 className="mx-auto text-muted-foreground mb-4" size={48} />
                <h3 className="text-lg font-semibold mb-2">No portfolios yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first portfolio to start tracking your investments
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="primary"
                    leftIcon={<Plus size={16} />}
                    onClick={() => navigate('/portfolio/create')}
                  >
                    Create Portfolio
                  </Button>
                  <Button
                    variant="secondary"
                    leftIcon={<Upload size={16} />}
                    onClick={() => setShowImportModal(true)}
                  >
                    Import CSV
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className={cn('grid grid-cols-1', isMobile ? 'gap-[12px]' : 'gap-4 md:grid-cols-2 xl:grid-cols-4')}>
              {portfolios?.map((portfolio) => (
                <Card
                  key={portfolio.portfolio_id}
                  variant="default"
                  padding="none"
                  className="group rounded-none border-[rgba(45,41,35,0.08)] bg-white shadow-none dark:border-[rgba(255,255,255,0.12)] dark:bg-[#1c1c1c]"
                >
                  <CardHeader className={cn(isMobile ? 'px-[16px] pt-[12px] pb-0' : 'px-5 pt-5 pb-0')}>
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-['Plus_Jakarta_Sans'] text-[16px] font-semibold leading-[24px] tracking-[0] text-[#2d2923] dark:text-[#fdfdfd]">
                        {portfolio.name}
                      </CardTitle>
                      {!isMobile && (
                        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => navigate(`/portfolio/${portfolio.portfolio_id}`)}
                          >
                            <Eye size={16} />
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => handleDeletePortfolio(portfolio.portfolio_id, portfolio.name)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className={cn(isMobile ? 'px-[16px] pb-[12px] pt-[14px]' : 'px-5 pb-5 pt-[18px]')}>
                    <div className={cn('grid grid-cols-2 gap-x-4', isMobile ? 'gap-y-[10px]' : 'gap-y-3')}>
                      <div className="flex flex-col">
                        <span className="text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Total Invested:</span>
                        <span className="text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">{formatMoney(portfolio.total_invested_amount)}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Profit/Loss:</span>
                        <span className={`text-[14px] font-normal leading-[20px] ${
                          (portfolio.total_gain_loss_usd || 0) >= 0 ? 'text-[#2ea66b]' : 'text-[#ff540c]'
                        }`}>
                          {formatMoney(portfolio.total_gain_loss_usd)} <span className="ml-1 text-[11px] font-semibold">{formatPercentage(portfolio.total_gain_loss_percentage, 0)}</span>
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Assets:</span>
                        <span className="text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">{portfolio.total_assets || 0}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Updated:</span>
                        <span className="text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">{new Date(portfolio.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className={cn(isMobile ? 'mt-[10px]' : 'mt-[18px]')}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-[32px] w-full rounded-none border-[rgba(45,41,35,0.12)] bg-white text-[12px] font-medium text-[#2d2923] dark:border-[rgba(255,255,255,0.12)] dark:bg-[#1c1c1c] dark:text-[#fdfdfd]"
                        onClick={() => navigate(`/portfolio/${portfolio.portfolio_id}`)}
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

        {/* Create Portfolio Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Create New Portfolio</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Portfolio Name</label>
                  <Input
                    value={newPortfolioName}
                    onChange={(e) => setNewPortfolioName(e.target.value)}
                    placeholder="Enter portfolio name"
                    onKeyPress={(e) => e.key === 'Enter' && handleCreatePortfolio()}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleCreatePortfolio}
                    disabled={creating || !newPortfolioName.trim()}
                    className="flex-1"
                  >
                    {creating ? 'Creating...' : 'Create'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Import CSV Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Import Portfolio from CSV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Portfolio Name</label>
                  <Input
                    value={csvPortfolioName}
                    onChange={(e) => setCsvPortfolioName(e.target.value)}
                    placeholder="Enter portfolio name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-input rounded-md"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Required columns: Project Name, Date of Investment, Invested Amount
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowImportModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleImportCSV}
                    disabled={importing || !csvFile || !csvPortfolioName.trim()}
                    className="flex-1"
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
    </PageTemplate>
  )
}

export default PortfolioTrackerScreen
